import { computed, inject, provide, ref, watch } from "vue";
import { useStore } from "vuex";

import { EmailTemplate, EmailTemplateCustomization } from "./emailTemplate";

// TODO: We have to force the same inject key in order to use this in iframes
export const injectKey = "email_templates";

export function useEmailTemplates(
  provideState = false,
  userParams?: Partial<{
    previewEmailTemplate: (
      customizations: EmailTemplateCustomization[]
    ) => Promise<string>;
    viewEmailTemplateNoCustomizations: () => Promise<string>;
  }>
) {
  const store = useStore();
  const loadingTemplates = ref(true);
  const loadingCustomizations = ref(false);
  const emailTemplates = ref<EmailTemplate[]>([]);
  const selectedTemplateKey = ref<string>();
  const templateCustomizations = ref<EmailTemplateCustomization[]>([]);
  const templateCustomizationsOriginalValue = ref(""); // JSON stringified version of the original to track changes
  const previewMode = ref(false);

  const selectedTemplate = computed(() => {
    return emailTemplates.value.find(
      (template) => template.key === selectedTemplateKey.value
    );
  });

  const templateCustomizationsChanged = computed(() => {
    if (!templateCustomizations.value) return false;
    if (!templateCustomizations.value.length) return false;
    return (
      JSON.stringify(templateCustomizations.value) !==
      templateCustomizationsOriginalValue.value
    );
  });

  async function refreshEmailTemplates() {
    loadingTemplates.value = true;
    try {
      emailTemplates.value = await store.dispatch("fetchEmailTemplates");
    } finally {
      loadingTemplates.value = false;
    }
  }

  async function getSelectedTemplateCustomizations() {
    if (!selectedTemplateKey.value) {
      templateCustomizations.value = [];
      return;
    }
    loadingCustomizations.value = true;
    try {
      const customizationsResponse = await store.dispatch(
        "fetchEmailTemplateCustomizations",
        selectedTemplateKey.value
      );
      snapshotCustomizations(customizationsResponse);
    } finally {
      loadingCustomizations.value = false;
    }
  }

  function snapshotCustomizations(
    customizations = templateCustomizations.value || []
  ) {
    const newCustomizations = customizations
      .map((row) => {
        const newRow = { ...row };
        if (row.data_type !== "html") return newRow;
        newRow.value = newRow.value || "";

        // Wrap each value with a <span> to match what the Quill Editor stores
        if (newRow.value !== "" && newRow.value.indexOf("<span>") !== 0) {
          newRow.value = `<span>${newRow.value}</span>`;
        }

        // Quill also strips away break tags. We should replace with new lines \n
        newRow.value = newRow.value.replace(/<br\s*\/?>\s*/gi, "\n");

        return newRow;
      })
      .map((val) => new EmailTemplateCustomization(val));
    templateCustomizations.value = newCustomizations;
    templateCustomizationsOriginalValue.value =
      JSON.stringify(newCustomizations);
  }

  function revertCustomizations() {
    templateCustomizations.value = (
      JSON.parse(templateCustomizationsOriginalValue.value || "") || []
    ).map((val) => new EmailTemplateCustomization(val));
  }

  // TODO: Use bulk endpoint for saving
  async function saveTemplateCustomizations() {
    const customizationsToSave = templateCustomizations.value.map((val) =>
      val.getData()
    );
    const deleteCalls = customizationsToSave
      .filter((val) => {
        return val.value === null;
      })
      .map((val) => {
        return store.dispatch("removeEmailTemplateCustomization", val);
      });
    const saveCalls = customizationsToSave
      .filter((val) => {
        return val.value !== null;
      })
      .map((val) => {
        return store.dispatch("saveEmailTemplateCustomization", val);
      });
    await Promise.all([...deleteCalls, ...saveCalls]);
    snapshotCustomizations();
  }

  async function previewEmailTemplate(data?: {
    templateKey: any;
    customizations: EmailTemplateCustomization[];
  }) {
    const templateKey = data?.templateKey || selectedTemplateKey.value;
    const customizations =
      data?.customizations || templateCustomizations.value || [];

    const results = userParams?.previewEmailTemplate
      ? await userParams.previewEmailTemplate(customizations)
      : await store.dispatch("previewEmailTemplate", {
          templateKey,
          customizations
        });
    return {
      templateKey,
      content: results
    };
  }

  async function viewEmailTemplateNoCustomizations(
    templateKey = selectedTemplateKey.value!
  ) {
    const results = userParams?.viewEmailTemplateNoCustomizations
      ? await userParams.viewEmailTemplateNoCustomizations()
      : await store.dispatch("viewEmailTemplateNoCustomizations", {
          templateKey: templateKey || ""
        });
    return {
      templateKey,
      content: results
    };
  }

  function resetCustomizationToDefault(
    customization: EmailTemplateCustomization
  ) {
    customization.value = customization.default;
  }

  function togglePreviewMode() {
    previewMode.value = !previewMode.value;
  }

  watch(selectedTemplateKey, () => {
    getSelectedTemplateCustomizations();
  });

  const emailTemplateInterface = {
    saveTemplateCustomizations,
    templateCustomizationsChanged,
    resetCustomizationToDefault,
    emailTemplates,
    refreshEmailTemplates,
    getSelectedTemplateCustomizations,
    previewMode,
    togglePreviewMode,
    templateCustomizations,
    previewEmailTemplate,
    selectedTemplateKey,
    loadingCustomizations,
    loadingTemplates,
    snapshotCustomizations,
    viewEmailTemplateNoCustomizations,
    selectedTemplate,
    revertCustomizations
  };

  if (provideState) provide(injectKey, emailTemplateInterface);

  return emailTemplateInterface;
}

export const injectUseEmailTemplates = () =>
  inject<ReturnType<typeof useEmailTemplates>>(injectKey)!;
